import { z } from 'zod';

/** Cara produk dikirim, cerminan enum ShippingMode di Prisma. */
export const SHIPPING_MODES = ['COURIER', 'PICKUP_ONLY', 'QUOTE'] as const;
export type ShippingMode = (typeof SHIPPING_MODES)[number];

/** Divisor volumetrik standar kurir Indonesia: p x l x t (cm) / 6000 = kg. */
export const VOLUMETRIC_DIVISOR = 6000;

/** Berat minimum yang ditagih kurir. Di bawah ini tetap dihitung 1 kg. */
export const MIN_BILLABLE_GRAM = 1000;

export const destinationSearchSchema = z.object({
  q: z.string().trim().min(3, 'Kata kunci minimal 3 karakter').max(80),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export const shippingQuoteSchema = z.object({
  destinationId: z.coerce.number().int().positive('Tujuan pengiriman belum dipilih'),
});

export const destinationSchema = z.object({
  id: z.number().int(),
  label: z.string(),
  provinceName: z.string(),
  cityName: z.string(),
  districtName: z.string(),
  subdistrictName: z.string(),
  zipCode: z.string(),
});

export const shippingOptionSchema = z.object({
  courier: z.string(),
  courierName: z.string(),
  service: z.string(),
  description: z.string(),
  cost: z.number().int(),
  etd: z.string().nullable(),
  group: z.enum(['reguler', 'express', 'kargo']),
});

export const shippingQuoteResultSchema = z.object({
  originId: z.number().int(),
  destinationId: z.number().int(),
  actualWeightGram: z.number().int(),
  volumetricWeightGram: z.number().int(),
  billableWeightGram: z.number().int(),
  options: z.array(shippingOptionSchema),
});

export type DestinationSearchQuery = z.infer<typeof destinationSearchSchema>;
export type ShippingQuoteInput = z.infer<typeof shippingQuoteSchema>;
export type Destination = z.infer<typeof destinationSchema>;
export type ShippingOption = z.infer<typeof shippingOptionSchema>;
export type ShippingQuoteResult = z.infer<typeof shippingQuoteResultSchema>;

/**
 * Jenis kegagalan hitung ongkir untuk pesan di web (fase B go-live):
 * - `upstream`: layanan ongkir sedang bermasalah (hulu gagal, server tidak terjangkau);
 *   sementara, jadi pembeli diberi tombol coba lagi.
 * - `no-service`: memang tidak ada kurir ke alamat ini; mencoba lagi tidak membantu.
 * - `other`: galat lain (sesi habis, keranjang kosong) yang pesannya ditampilkan apa adanya.
 */
export type ShippingQuoteProblem = 'upstream' | 'no-service' | 'other';

export function shippingQuoteProblem(error: unknown): ShippingQuoteProblem {
  if (error === null || typeof error !== 'object') return 'upstream';
  const { status, code } = error as { status?: unknown; code?: unknown };
  if (code === 'SHIPPING_NO_SERVICE') return 'no-service';
  if (code === 'SHIPPING_UPSTREAM_ERROR') return 'upstream';
  if (typeof status === 'number') return status >= 500 ? 'upstream' : 'other';
  // Bukan jawaban API (fetch gagal di jaringan): dari sisi pembeli sama dengan gangguan.
  return 'upstream';
}
